import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  DataTable,
  Badge,
  HighlightCard,
  SegmentedControl,
  Skeleton,
  StatusMessage,
} from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Filters, Pagination } from '../../components/uiChrome.jsx';
import { formatRelativeTime, CHIP_BASE, CHIP_LABEL, CHIP_TONE } from '../../utils';
import { useInstanceUsage } from '../../hooks/instance';
import { Activity, AlertTriangle, Bot, Gauge, LayoutGrid, Share2, Timer, Users, Workflow } from 'lucide-react';

const IMPACT_TILES = [
  {
    key: 'workflows',
    icon: Workflow,
    variant: 'primary',
    helperKey: 'workflowsHelper',
    iconHighlight: { bgColor: 'Primary', iconColor: 'PrimaryAlt', size: 'small' },
  },
  {
    key: 'agents',
    icon: Bot,
    variant: 'purple',
    helperKey: 'agentsHelper',
    iconHighlight: { bgColor: 'Purple', iconColor: 'Purple', size: 'small' },
  },
  {
    key: 'networks',
    icon: Share2,
    variant: 'orange',
    helperKey: 'networksHelper',
    iconHighlight: { bgColor: 'Alert', iconColor: 'Alert', size: 'small' },
  },
  {
    key: 'widgets',
    icon: LayoutGrid,
    variant: 'green',
    helperKey: 'widgetsHelper',
    iconHighlight: { bgColor: 'Success', iconColor: 'Green', size: 'small' },
  },
];

// Usage stat tiles (CT-4193 AC) — Total calls / Active consumers / Success rate /
// Avg latency. Rendered with `HighlightCard` (same family as the impact row) so
// every metric in the tab reads as one consistent tile style. Values (and the
// `%`/`ms` units) come from the derived `stats` at render.
const STAT_TILES = [
  {
    key: 'totalCalls',
    icon: Activity,
    variant: 'primary',
    iconHighlight: { bgColor: 'Primary', iconColor: 'PrimaryAlt', size: 'small' },
  },
  {
    key: 'activeConsumers',
    icon: Users,
    variant: 'purple',
    iconHighlight: { bgColor: 'Purple', iconColor: 'Purple', size: 'small' },
  },
  {
    key: 'successRate',
    icon: Gauge,
    variant: 'green',
    unit: '%',
    iconHighlight: { bgColor: 'Success', iconColor: 'Green', size: 'small' },
  },
  {
    key: 'avgLatency',
    icon: Timer,
    variant: 'orange',
    unit: 'ms',
    iconHighlight: { bgColor: 'Alert', iconColor: 'Alert', size: 'small' },
  },
];

const USAGE_PERIODS = [7, 30, 90];

const TYPE_PLURAL = { workflow: 'workflows', agent: 'agents', network: 'networks', widget: 'widgets' };
const TYPE_OPTIONS = ['agent', 'workflow', 'user'];
const STUDIO_OPTIONS = ['agentic', 'workforce', 'governance'];
const TYPE_TONE = { agent: 'purple', workflow: 'accent', user: 'neutral' };
const DEFAULT_PAGE_SIZE = 10;

// Success-rate text color by health band (green ≥95, amber ≥85, else red).
const successRateTone = (rate) =>
  rate >= 95 ? 'text-emerald-400' : rate >= 85 ? 'text-amber-400' : 'text-red-400';

/** Minimal token-based daily call-volume chart (CT-4193). Bars scale to the
 * period's peak; no charting dependency. Pure — re-renders only with its props. */
function UsageVolumeChart({ series, period, t }) {
  if (series.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-content-muted">
        {t('integrations.instance.usage.chart.empty')}
      </p>
    );
  }
  const maxCalls = series.reduce((m, p) => Math.max(m, p.calls), 0);
  return (
    <div
      role="img"
      aria-label={t('integrations.instance.usage.chart.aria', { period })}
      className="flex h-28 items-end gap-[3px] rounded-lg border border-edge px-3 py-3"
    >
      {series.map((pt) => {
        const h = maxCalls ? Math.max(3, Math.round((pt.calls / maxCalls) * 100)) : 3;
        return (
          <div
            key={pt.day}
            className="flex-1 rounded-t-[2px] bg-[#2B7FFF]/70 transition-colors hover:bg-[#2B7FFF]"
            style={{ height: `${h}%` }}
            title={`${pt.day} · ${pt.calls}`}
          />
        );
      })}
    </div>
  );
}

/**
 * Instance Detail — Usage tab (CT-4193, admin-only). Two stacked cards: usage
 * analytics (stat tiles + daily call-volume chart with a 7/30/90 period selector
 * + capability breakdown) and, separately, Consumers (impact tiles + searchable,
 * paginated consumer table). Every metric uses `HighlightCard` for one tile style.
 * No prototype counterpart for this tab — built to the CT-4193 acceptance criteria.
 */
const InstanceUsageTab = () => {
  const { t } = useTranslation('admin-studio');
  const { instance } = useOutletContext();
  const [period, setPeriod] = useState(30);
  const { stats, series, capabilities, consumers, now, isLoading, error } = useInstanceUsage(
    instance.instanceId,
    period,
  );

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);
  const [studioFilter, setStudioFilter] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);

  const searchProps = useMemo(
    () => ({
      value: search,
      onChange: (e) => {
        setSearch(e?.target ? e.target.value : e);
        setPage(1);
      },
      onClear: () => {
        setSearch('');
        setPage(1);
      },
      placeholder: t('integrations.instance.usage.searchPlaceholder'),
    }),
    [search, t],
  );

  const filtersConfig = useMemo(
    () => [
      {
        id: 'type',
        label: t('integrations.instance.usage.typeFilter.all'),
        multiple: false,
        value: typeFilter,
        onChange: (next) => {
          setTypeFilter(next);
          setPage(1);
        },
        options: TYPE_OPTIONS.map((v) => ({
          value: v,
          label: t(`integrations.instance.usage.type.${v}`),
        })),
      },
      {
        id: 'studio',
        label: t('integrations.instance.usage.studioFilter.all'),
        multiple: false,
        value: studioFilter,
        onChange: (next) => {
          setStudioFilter(next);
          setPage(1);
        },
        options: STUDIO_OPTIONS.map((v) => ({
          value: v,
          label: t(`integrations.instance.usage.studioFilter.${v}`),
        })),
      },
    ],
    [typeFilter, studioFilter, t],
  );

  const byType = useMemo(() => {
    const counts = { workflows: 0, agents: 0, networks: 0, widgets: 0 };
    for (const row of consumers) {
      const key = TYPE_PLURAL[row.type];
      if (key) counts[key] += 1;
    }
    return counts;
  }, [consumers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const typeValue = typeFilter[0];
    const studioValue = studioFilter[0];
    return consumers.filter((r) => {
      if (typeValue && r.type !== typeValue) return false;
      if (studioValue && r.studio !== studioValue) return false;
      if (q && !`${r.name} ${r.type}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [consumers, search, typeFilter, studioFilter]);

  const columns = useMemo(
    () => [
      {
        key: 'last_used',
        label: t('integrations.instance.usage.columns.lastUsed'),
        headerClassName: 'w-[140px]',
        render: (v) => (
          <span className="text-[13px] text-content-secondary" title={v ? new Date(v).toLocaleString() : undefined}>
            {v ? formatRelativeTime(v, t, now || undefined) : '—'}
          </span>
        ),
      },
      { key: 'name', label: t('integrations.instance.usage.columns.consumer') },
      {
        key: 'type',
        label: t('integrations.instance.usage.columns.type'),
        headerClassName: 'w-[128px]',
        render: (v) => (
          <Badge variant="neutral" size="small" className={`${CHIP_BASE} ${CHIP_TONE[TYPE_TONE[v] ?? 'neutral']}`}>
            <span className={CHIP_LABEL}>{t(`integrations.instance.usage.type.${v}`)}</span>
          </Badge>
        ),
      },
      {
        key: 'studio',
        label: t('integrations.instance.usage.columns.studio'),
        headerClassName: 'w-[120px]',
        render: (v) => t(`integrations.instance.usage.studioFilter.${v}`),
      },
      {
        key: 'uses',
        label: t('integrations.instance.usage.columns.uses'),
        headerClassName: 'w-[100px]',
        render: (v) => <span className="font-mono text-[11px] text-content">{v}</span>,
      },
    ],
    [t, now],
  );

  const capColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('integrations.instance.usage.capabilities.columns.capability'),
        render: (v) => <code className="font-mono text-[12px] text-content">{v}</code>,
      },
      {
        key: 'calls',
        label: t('integrations.instance.usage.capabilities.columns.calls'),
        headerClassName: 'w-[120px]',
        render: (v) => <span className="font-mono text-[11px] text-content">{v.toLocaleString()}</span>,
      },
      {
        key: 'success_rate',
        label: t('integrations.instance.usage.capabilities.columns.successRate'),
        headerClassName: 'w-[130px]',
        render: (v) => <span className={`font-mono text-[11px] ${successRateTone(v)}`}>{v}%</span>,
      },
    ],
    [t],
  );

  const statValues = {
    totalCalls: stats.total_calls ?? 0,
    activeConsumers: stats.active_consumers ?? consumers.length,
    successRate: stats.success_rate ?? 0,
    avgLatency: stats.avg_latency_ms ?? 0,
  };

  // Only block on the first load — a period re-fetch keeps the chrome (and the
  // period control) mounted so it doesn't flash the skeleton on every change.
  if (isLoading && !now) return <Skeleton className="h-40 w-full" />;
  if (error) return <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />;

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * perPage;
  const pageRows = filtered.slice(pageStart, pageStart + perPage);
  const rangeLabel =
    filtered.length === 0 ? '0' : `${pageStart + 1}–${Math.min(pageStart + perPage, filtered.length)}`;

  return (
    <div className="flex flex-col gap-4">
      {/* Usage analytics — stat tiles, call-volume chart, capability breakdown. */}
      <Card>
        <Card.Body className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            {STAT_TILES.map((tile) => (
              <HighlightCard
                key={tile.key}
                variant={tile.variant}
                icon={tile.icon}
                iconHighlight={tile.iconHighlight}
                label={t(`integrations.instance.usage.stats.${tile.key}`, { period })}
                value={statValues[tile.key]}
                unit={tile.unit}
                showUnit={tile.unit != null}
                feedback={t(`integrations.instance.usage.statsHelper.${tile.key}`)}
              />
            ))}
          </div>

          <div className="flex flex-col gap-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-content">
                {t('integrations.instance.usage.chart.title')}
              </h3>
              <SegmentedControl
                value={String(period)}
                onChange={(next) => setPeriod(Number(next))}
                ariaLabel={t('integrations.instance.usage.chart.periodAria')}
                options={USAGE_PERIODS.map((p) => ({
                  value: String(p),
                  label: t(`integrations.instance.usage.period.${p}`),
                }))}
              />
            </div>
            <UsageVolumeChart series={series} period={period} t={t} />
          </div>

          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-content">
              {t('integrations.instance.usage.capabilities.title')}
            </h3>
            <DataTable
              variant="workspace"
              columns={capColumns}
              data={capabilities}
              minTableWidth={480}
              emptyMessage={t(
                period === 90
                  ? 'integrations.instance.usage.capabilities.emptyNone'
                  : 'integrations.instance.usage.capabilities.empty',
              )}
            />
          </div>
        </Card.Body>
      </Card>

      {/* Consumers — who has called this instance, in its own container. */}
      <Card>
        <Card.Body className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
            {IMPACT_TILES.map((tile) => {
              const value = byType[tile.key] ?? 0;
              return (
                <HighlightCard
                  key={tile.key}
                  variant={tile.variant}
                  icon={tile.icon}
                  iconHighlight={tile.iconHighlight}
                  label={t(`integrations.instance.usage.tiles.${tile.key}`)}
                  value={value}
                  showUnit={false}
                  feedback={t(`integrations.instance.usage.tiles.${tile.helperKey}`)}
                  className={value === 0 ? 'opacity-55' : undefined}
                />
              );
            })}
          </div>

          <div className="flex flex-wrap items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-content">{t('integrations.instance.usage.title')}</h3>
            <span className="text-[12px] text-content-muted">{t('integrations.instance.usage.scope')}</span>
          </div>

          <Filters
            searchProps={searchProps}
            filters={filtersConfig}
            showAllFilters={false}
            maxFilters={2}
            onClearAll={() => {
              setSearch('');
              setTypeFilter([]);
              setStudioFilter([]);
              setPage(1);
            }}
          />

          <DataTable
            variant="workspace"
            columns={columns}
            data={pageRows}
            minTableWidth={880}
            emptyMessage={
              consumers.length === 0
                ? t('integrations.instance.usage.emptyNone')
                : t('integrations.instance.usage.empty')
            }
          />

          <Pagination
            rangeLabel={rangeLabel}
            totalItems={filtered.length}
            currentPage={safePage}
            totalPages={totalPages}
            perPage={perPage}
            hasPrevious={safePage > 1}
            hasNext={safePage < totalPages}
            onPrevPage={() => setPage(Math.max(1, safePage - 1))}
            onNextPage={() => setPage(Math.min(totalPages, safePage + 1))}
            onPageSizeChange={(size) => {
              setPerPage(size);
              setPage(1);
            }}
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default InstanceUsageTab;
